/**
 * THE PROBLEMS PANEL — the errors aspect's half of the board.
 *
 * It lists what `experienceProblems` found and navigates to it. It computes nothing: every row is a
 * `BlockIssue` a block raised about itself, or a flow card problem the task's own flow panel would
 * show, so the panel and the card can never word the same fault differently.
 *
 * ## Grouped by task, outlined, and silent about itself
 *
 * One outlined group per task, in board order, because a long flat list blends together. No
 * explanatory text: a panel that has to tell you how it works has the wrong labels.
 *
 * ## Why rows own the navigation
 *
 * In the Scoring aspect a card click means SELECT, because its verb is "apply to selected". Here
 * the verb is GO TO, so the card keeps its ordinary click and the rows navigate — otherwise the
 * two would fight over the same gesture.
 */
import { AlertTriangle, ChevronRight, OctagonAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ExperienceProblem, ProblemGroup, ProblemSeverityFilter } from '@/lib/experience-problems';

import { SearchField } from '@/components/ui/search-field';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { blockLabelWithType, filterGroups } from '@/lib/experience-problems';

export interface ProblemsPanelProps {
  groups: ProblemGroup[];
  errors: number;
  warnings: number;
  /**
   * Which row the pointer is over, so the board can bring that task into view and light the block
   * it names. HOVER rather than selection: the owner asked why it took two clicks to reach a
   * problem, and the honest answer was that the first click was buying a preview the pointer was
   * already offering for free.
   */
  hoveredId?: string;
  onHover: (problem: ExperienceProblem | null) => void;
  /** Open the task this problem lives in, with its control focused where one is named. */
  onGoTo: (problem: ExperienceProblem) => void;
  /** Expand a pathway whose tasks are collapsed out of the board, so its cards can be marked. */
  onExpandPathway?: (pathwayId: string) => void;
  /**
   * Where to remember the filter, which is the experience's id.
   *
   * Per experience and per tab, for the same reason the aspect itself is: repairing a problem
   * means leaving for the task and coming back, and a filter that reset there would have to be
   * retyped after every single fix.
   */
  storageKey?: string;
}

const filterStore = (key: string) => `agavi.problemsFilter.${key}`;

function readFilter(key?: string): { query: string; severity: ProblemSeverityFilter } {
  const blank = { query: '', severity: 'all' as ProblemSeverityFilter };
  if (!key) return blank;
  try {
    const raw = sessionStorage.getItem(filterStore(key));
    if (!raw) return blank;
    const v = JSON.parse(raw) as Partial<{ query: string; severity: ProblemSeverityFilter }>;
    return {
      query: typeof v.query === 'string' ? v.query : '',
      severity: v.severity === 'error' || v.severity === 'warning' ? v.severity : 'all',
    };
  } catch { return blank; }
}

export default function ProblemsPanel({
  groups, errors, warnings, hoveredId, onHover, onGoTo, onExpandPathway, storageKey,
}: ProblemsPanelProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState(() => readFilter(storageKey));
  const setAndKeep = (next: typeof filter) => {
    setFilter(next);
    if (!storageKey) return;
    try { sessionStorage.setItem(filterStore(storageKey), JSON.stringify(next)); } catch { /* private mode */ }
  };
  const shown = useMemo(() => filterGroups(groups, filter), [groups, filter]);
  const total = groups.reduce((n, g) => n + g.rows.length, 0);
  const count = shown.reduce((n, g) => n + g.rows.length, 0);
  const filtering = !!filter.query.trim() || filter.severity !== 'all';

  /**
   * FOCUS FOLLOWS THE PANEL IN.
   *
   * Pressing "Errors, 107" opened this and left focus on the button behind it, so a keyboard
   * user then tabbed the length of the board to reach the list they had just asked for -- 479
   * focusable things on that page. Measured: `document.activeElement` was BODY after the open.
   *
   * The heading rather than the first row: the panel's own name is what orients someone who has
   * just arrived, and a reader lands on "Problems" and can then walk the list. `tabIndex={-1}`
   * makes it focusable without putting a heading into the tab order for everyone else.
   */
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { headingRef.current?.focus(); }, []);

  return (
    <div className='flex min-h-0 flex-col'>
      <div className='flex-shrink-0 border-b border-ws-border px-4 pb-3 pt-4'>
        {/* The count of what the filter left rides with the title, which is the one line that has
            room for it — and only while a filter is on, because a list showing everything does not
            need to say so. Live, because the change it reports happens below where a typist is
            looking. */}
        <div className='flex items-baseline gap-2'>
          <h2 ref={headingRef} tabIndex={-1} className='flex-1 text-base font-bold outline-none'>{t('experience.problems.title')}</h2>
          <span aria-live='polite' className='text-xs font-semibold text-ws-muted'>
            {filtering ? t('experience.problems.showing', { count, total }) : ''}
          </span>
        </div>
        {/* THE GATE, stated as the consequence rather than as a count: "7 problems" does not say
            whether the experience can ship, and that is the only question this panel settles. */}
        <div className={`mt-3 flex items-start gap-2.5 rounded-lg px-3 py-2.5 ${
          errors > 0 ? 'bg-ws-error-bg text-ws-error-fg' : 'bg-ws-warning-bg text-ws-warning-fg'}`}>
          {errors > 0
            ? <OctagonAlert aria-hidden='true' className='mt-0.5 h-[18px] w-[18px] shrink-0' />
            : <AlertTriangle aria-hidden='true' className='mt-0.5 h-[18px] w-[18px] shrink-0' />}
          <p className='text-sm font-bold leading-snug'>
            {errors > 0
              ? t('experience.problems.errorsBlock', { count: errors })
              : t('experience.problems.warningsOnly', { count: warnings })}
          </p>
        </div>

        {/* ONE BOX AND ONE SWITCH, on one line. The box matches the row's own words — the fault,
            the field, the block, the task — so "alt", "flow" and "MCQ 1" all narrow the list
            without anyone maintaining a list of kinds. The switch is the one cut nobody can type:
            what still has to be fixed before this can be published. */}
        <div className='mt-3 flex items-center gap-2'>
          <div className='min-w-0 flex-1'>
            {/* The clear control is INSIDE the box, because it belongs to the text rather than to
                the row: beside it, it moved the switch along every time a query appeared. That
                arrangement is now `SearchField`'s, shared with every other search box in the app. */}
            <SearchField
              value={filter.query}
              onChange={(query) => setAndKeep({ ...filter, query })}
              ariaLabel={t('experience.problems.filterLabel')}
              /* "Clear filter", not "Clear search": this panel calls the box a filter in its own
                 heading, and a name that disagrees with the word on screen is worse than a
                 generic one. */
              clearLabel={t('experience.problems.filterClear')}
            />
          </div>
          <SegmentedControl
            aria-label={t('experience.problems.filterLabel')}
            value={filter.severity}
            onChange={(severity) => setAndKeep({ ...filter, severity: severity as typeof filter.severity })}
            options={[
              { id: 'all', label: t('experience.problems.filterAll') },
              /* The count is the label and the icon carries the severity, so the switch says what
                 is left to fix without a second row of text saying it again. */
              { id: 'error', label: String(errors), ariaLabel: t('experience.problems.filterErrors', { count: errors }),
                icon: <OctagonAlert aria-hidden='true' className='h-3.5 w-3.5 text-ws-error-fg' /> },
              { id: 'warning', label: String(warnings), ariaLabel: t('experience.problems.filterWarnings', { count: warnings }),
                icon: <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 text-ws-warning-fg' /> },
            ]}
          />
        </div>
      </div>

      <div className='min-h-0 flex-1 overflow-y-auto px-3 pb-4 pt-2'>
        {filtering && !shown.length && (
          <p className='px-2 py-6 text-center text-sm text-ws-muted'>{t('experience.problems.noMatches')}</p>
        )}
        {shown.map((g) => (
          <section key={g.taskId}
            className='mb-3 rounded-lg border border-ws-border bg-ws-surface'
            aria-label={g.title || t('nav.newExperience')}>
            <header className='flex items-center gap-2 border-b border-ws-border px-3 py-2'>
              <span className='min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wide text-ws-muted'>
                {g.title || t('nav.newExperience')}
              </span>
              {g.errors > 0 && (
                <span className='flex items-center gap-1 text-xs font-bold text-ws-error-fg'>
                  <OctagonAlert aria-hidden='true' className='h-3 w-3' />{g.errors}
                </span>
              )}
              {g.warnings > 0 && (
                <span className='flex items-center gap-1 text-xs font-bold text-ws-warning-fg'>
                  <AlertTriangle aria-hidden='true' className='h-3 w-3' />{g.warnings}
                </span>
              )}
            </header>

            {/* A COLLAPSED PATHWAY has no cards on the board to mark, so its group opens the
                pathway rather than pretending a mark is waiting. */}
            {g.collapsedInto && onExpandPathway ? (
              <button type='button' onClick={() => onExpandPathway(g.collapsedInto!)}
                className='flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-ws-text hover:bg-ws-hover'>
                <span className='flex-1'>{t('experience.problems.inCollapsedPathway', { count: g.rows.length })}</span>
                <ChevronRight aria-hidden='true' className='h-4 w-4 shrink-0 text-ws-muted' />
              </button>
            ) : (
              <ul className='p-1.5'>
                {g.rows.map((r) => {
                  const isError = r.issue.severity === 'error';
                  const Mark = isError ? OctagonAlert : AlertTriangle;
                  const on = r.id === hoveredId;
                  return (
                    <li key={r.id}>
                      {/* ONE CLICK GOES. Keyboard focus previews on the same terms a pointer does,
                          so tabbing the list lights each task in turn rather than making a reader
                          choose between seeing where a problem is and going to it. */}
                      <button type='button'
                        onClick={() => onGoTo(r)}
                        onMouseEnter={() => onHover(r)}
                        onMouseLeave={() => onHover(null)}
                        onFocus={() => onHover(r)}
                        onBlur={() => onHover(null)}
                        className={`flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left ${
                          on ? 'bg-ws-primary-subtle' : 'hover:bg-ws-hover'}`}>
                        <Mark aria-hidden='true'
                          className={`mt-0.5 h-4 w-4 shrink-0 ${isError ? 'text-ws-error-fg' : 'text-ws-warning-fg'}`} />
                        <span className='min-w-0 flex-1'>
                          <span className='block text-[13.5px] leading-snug text-ws-text'>{r.issue.detail}</span>
                          {/* WHERE, then the block, then the field within it. "Question text is
                              blank" under "Question" told a teacher nothing they could act on;
                              under "MCQ 1 · Question" it names the one block of three they have to
                              open. The place is only there when it is not the task's own canvas,
                              and it leads, because somewhere else entirely is the first thing a
                              reader needs: "Summary feedback · Sound · Transcript". A row with no
                              block of its own — flow, routing — keeps the field alone, which is
                              already the only thing there is to say. */}
                          <span className='mt-0.5 block text-xs text-ws-muted'>
                            {[r.scope, blockLabelWithType(r), r.issue.category].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <ChevronRight aria-hidden='true'
                          className={`mt-0.5 h-4 w-4 shrink-0 ${on ? 'text-ws-primary' : 'text-ws-border'}`} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
